import { useState, useCallback, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { getTripRoute } from '@/api/routes';
import { buildFleetTripPatchFromRoute } from '@/utils/fleetTripSync';

function routeOriginForAlias(alias: string): { lat: number; lon: number } | null {
  const state = useMapStore.getState();
  const live = state.livePosition;
  if (live?.alias === alias && live.lat != null && live.lon != null) {
    return { lat: live.lat, lon: live.lon };
  }

  const feature = state.vehicleFeatures.find((item) => item.properties.alias === alias);
  if (feature?.geometry.type === 'Point') {
    const [lon, lat] = feature.geometry.coordinates;
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }
  }

  return null;
}

/**
 * Hook para manejar la ruta de un viaje.
 *
 * IMPORTANTE: NUNCA se hace setRouteData(null) durante loading/refresh.
 * Hacerlo resetea drawnAliasRef en RouteMapLayer, lo que causa fitBounds
 * espurio cuando la data nueva llega. Solo closeRoute limpia la ruta.
 */
export function useTripRoute() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const setRouteData = useMapStore((s) => s.setRouteData);
  const routeData = useMapStore((s) => s.routeData);
  const syncVehicleFeature = useMapStore((s) => s.syncVehicleFeature);
  const setRoutePanelOpen = useUIStore((s) => s.setRoutePanelOpen);
  const syncFleetTrip = useUIStore((s) => s.syncFleetTrip);

  const currentAliasRef = useRef<string | null>(null);

  const fetchRoute = useCallback(
    async (alias: string) => {
      setLoading(true);
      setError(null);
      // NO setRouteData(null) — mantener ruta existente mientras carga

      try {
        const origin = routeOriginForAlias(alias);
        const data = await getTripRoute(alias, false, origin?.lat, origin?.lon);
        if (!data.success || (!data.polyline?.length && !data.is_at_destination)) {
          setError(data.error || 'Ruta sin datos de polilínea');
          return;
        }
        setRouteData({ ...data, client_received_at_ms: Date.now() });
        if (data.alias) {
          const routePatch = buildFleetTripPatchFromRoute(data);
          syncFleetTrip(data.alias, routePatch);
          syncVehicleFeature(data.alias, routePatch);
        }
        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar ruta');
      } finally {
        setLoading(false);
      }
    },
    [setRouteData, syncFleetTrip, syncVehicleFeature]
  );

  const showRoute = useCallback(
    async (alias: string) => {
      // Si es el mismo alias que ya está abierto, no hacer nada
      if (currentAliasRef.current === alias && routeData?.alias === alias) {
        setRoutePanelOpen(true);
        return;
      }
      currentAliasRef.current = alias;
      setRoutePanelOpen(true);
      await fetchRoute(alias);
    },
    [setRoutePanelOpen, fetchRoute, routeData]
  );

  const refreshRoute = useCallback(async () => {
    // Always use the alias from the currently displayed route
    const alias = useMapStore.getState().routeData?.alias || currentAliasRef.current;
    if (!alias) return;
    setLoading(true);
    setError(null);
    // NO setRouteData(null) — la ruta vieja se queda visible mientras carga

    try {
      const livePosition = useMapStore.getState().livePosition;
      const origin = livePosition?.alias === alias
        ? { lat: livePosition.lat ?? undefined, lon: livePosition.lon ?? undefined }
        : routeOriginForAlias(alias) ?? undefined;
      const data = await getTripRoute(
        alias,
        true,
        origin?.lat,
        origin?.lon,
      );
      if (!data.success || (!data.polyline?.length && !data.is_at_destination)) {
        setError(data.error || 'Ruta sin datos de polilínea');
        return;
      }
      setRouteData({ ...data, client_received_at_ms: Date.now() });
      if (livePosition?.lat != null && livePosition?.lon != null) {
        useMapStore.getState().setLivePosition({
          ...livePosition,
          off_route: false,
          distance_to_route_m: 0,
          progress_km: 0,
          progress_percent: 0,
        });
      }
      if (data.alias) {
        const routePatch = buildFleetTripPatchFromRoute(data);
        syncFleetTrip(data.alias, routePatch);
        syncVehicleFeature(data.alias, routePatch);
      }
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar ruta');
    } finally {
      setLoading(false);
    }
  }, [setRouteData, syncFleetTrip, syncVehicleFeature]);

  const closeRoute = useCallback(() => {
    currentAliasRef.current = null;
    setRoutePanelOpen(false);
    setRouteData(null); // Solo aquí se limpia — al cerrar
    setLastUpdated(null);
  }, [setRoutePanelOpen, setRouteData]);

  const currentAlias = currentAliasRef.current;

  return { routeData, loading, error, lastUpdated, showRoute, closeRoute, refreshRoute, currentAlias };
}
